import React, { useCallback, useEffect, useRef, useState } from "react";
import { sortBy } from "lodash";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Close } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";
import { InsightResult, PenPortraitInsightVariable } from "@/types";
import { blackOverlay, interpolateDivergant, toIconLabel, toIndex, toPercentage, whiteOverlay } from "@/utils";

interface TopSitesCardProps {
  title: string;
  variables: PenPortraitInsightVariable[];
  score: string;
}

const TopSitesCard: React.FC<TopSitesCardProps> = ({
  title,
  variables,
  score,
}) => {
  const theme = useTheme();

  const [scores, setScores] = useState<PenPortraitInsightVariable[]>([]);
  const formatFunc = score === "rate" ? toPercentage : toIndex;

  const maxValue = Math.max.apply(
    Math,
    scores.slice(0, 100)
      .map((x) => Number(x[score as keyof PenPortraitInsightVariable])),
  );
  const minValue = Math.min.apply(
    Math,
    scores.slice(0, 100)
      .map((x) => Number(x[score as keyof PenPortraitInsightVariable])),
  );

  const calcPercentage = (value: number) => {
    const mx = maxValue ? maxValue : 1;
    const mn = minValue ? minValue : 0;
    return Math.max(0.01, (value - mn) / (mx - mn));
  };

  useEffect(() => {
    let filtered = variables
      .filter((x) =>
        score === "rate"
          ? Number(x[score as keyof PenPortraitInsightVariable]) > 0
          : Number(x[score as keyof PenPortraitInsightVariable]) > 0,
      )
    setScores(sortBy(filtered, score).reverse());
  }, [score, variables]);

  if (scores.length === 0) {
    return null;
  } else {
    return (
      <Card
        key={title}
        sx={{
          borderRadius: 1,
          borderColor: theme.palette.divider,
          width: "100%",
          containerType: "inline-size",
          position: "relative",
          height: 602,
          overflowY: "auto"
        }}
      >
        <CardHeader
          title={title}
          slotProps={{ title: { variant: "body2" } }}
          sx={{ height: "56px", alignItems: "flex-start", pb: 0 }}
        />
        <CardContent sx={{ pb: 2, "&:last-child": { pb: 2 } }}>
          {scores.slice(0, 20).map((site, index) => {
            const value = Number(site[score as keyof PenPortraitInsightVariable])
              return (
                <Stack key={index} spacing={0} px={1} mb={2}>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: "1",
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {site.tag_name}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={3}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box
                      height="4px"
                      borderRadius="4px"
                      width={`${calcPercentage(value) * 100}%`}
                      sx={{
                        backgroundColor: interpolateDivergant(
                          theme.palette.score.light,
                          theme.palette.score.main,
                          theme.palette.score.dark,
                          1 - calcPercentage(value),
                        ),
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: interpolateDivergant(
                          theme.palette.score.light,
                          theme.palette.score.main,
                          theme.palette.score.dark,
                          1 - calcPercentage(value),
                        ),
                      }}
                    >
                      {formatFunc(value)}
                    </Typography>
                  </Stack>
                </Stack>
              )
          })}
        </CardContent>
      </Card>
    );
  }
};

export default TopSitesCard;
